import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, assetsAPI, usersAPI, workflowsAPI, schedulesAPI, sopsAPI, reportsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import {
  Plus, Search, Filter, RefreshCw, X, ClipboardList, AlertTriangle,
  Clock, User, Package, FileText, Image, Video, ChevronRight, Calendar as CalendarIcon,
  Edit2, Copy, Trash2, MoreVertical, CheckCircle, List, BookOpen, Download, Eye
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import JobsCalendar from '../components/jobs/JobsCalendar';
import PMSchedulesModal from '../components/jobs/PMSchedulesModal';
import ReportViewerModal from '../components/jobs/ReportViewerModal';

const Badge = ({ s }) => <span className={`badge badge-${s}`}>{s?.replace('_',' ')}</span>;
const PBadge = ({ p }) => <span className={`badge badge-${p}`}>{p}</span>;

const PRIORITY_COLORS = { critical:'#EF4444', high:'#F97316', medium:'#F59E0B', low:'#10B981' };

function JobFormModal({ job, onClose, onSave }) {
  const isEdit = !!job;
  const [form, setForm] = useState({ 
    title: job?.title || '', 
    description: job?.description || '', 
    workflow_template_id: job?.workflow_template_id || '', 
    asset_id: job?.asset_id || '', 
    assigned_to: job?.assigned_to || '', 
    priority: job?.priority || 'medium', 
    due_date: job?.due_date ? format(parseISO(job.due_date), "yyyy-MM-dd'T'HH:mm") : '', 
    notes: job?.notes || '', 
    location: job?.location || '',
    status: job?.status || (isEdit ? 'pending' : 'pending')
  });
  const [files, setFiles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkedSops, setLinkedSops] = useState([]);

  useEffect(() => {
    Promise.all([assetsAPI.list({limit:500}), usersAPI.technicians(), workflowsAPI.list({active:true})]).then(([a,t,w]) => {
      setAssets(a.data.assets);
      setTechnicians(t.data.users);
      setWorkflows(w.data.workflows);
    });
  }, []);

  useEffect(() => {
    if (form.asset_id) {
      sopsAPI.getForAsset(form.asset_id).then(r => setLinkedSops(r.data.sops)).catch(() => setLinkedSops([]));
    } else {
      setLinkedSops([]);
    }
  }, [form.asset_id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [], 'video/*': [], 'application/pdf': [] },
    onDrop: (f) => setFiles(prev => [...prev, ...f])
  });

  const save = async () => {
    if (!form.title) return toast.error('Job title required');
    setLoading(true);
    try {
      if (isEdit) {
        await jobsAPI.update(job.id, form);
        toast.success('Job updated!');
      } else {
        const fd = new FormData();
        Object.entries(form).forEach(([k,v]) => v && fd.append(k, v));
        files.forEach(f => fd.append('reference_documents', f));
        await jobsAPI.create(fd);
        toast.success('Job created!');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEdit?'update':'create'} job`);
    } finally { setLoading(false); }
  };

  const fileIcon = (f) => f.type.startsWith('video') ? <Video size={14} /> : f.type.startsWith('image') ? <Image size={14} /> : <FileText size={14} />;

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl">
        <div className="modal-header">
          <div>
            <div className="modal-title">{isEdit ? 'Edit Job' : 'Create New Job'}</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>
              {isEdit ? `Modifying ${job.job_number}` : 'Configure job details, workflow, and attach reference materials'}
            </div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            {/* Left col */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-group">
                <label className="form-label">Job Title *</label>
                <input className="form-input" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Monthly HVAC Inspection" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Job scope and instructions..." rows={3} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                    {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-input" type="datetime-local" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))} />
                </div>
              </div>
              {isEdit && (
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                    {['pending','assigned','in_progress','completed','overdue','cancelled'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Assign Asset</label>
                <select className="form-select" value={form.asset_id} onChange={e=>setForm(f=>({...f,asset_id:e.target.value}))}>
                  <option value="">No specific asset</option>
                  {assets.map(a => <option key={a.id} value={a.id}>{a.name} ({a.asset_code})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Assign Technician</label>
                <select className="form-select" value={form.assigned_to} onChange={e=>setForm(f=>({...f,assigned_to:e.target.value}))}>
                  <option value="">Unassigned</option>
                  {technicians.map(t => <option key={t.id} value={t.id}>{t.name} — {t.department || 'No dept'}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Workflow Template</label>
                <select className="form-select" value={form.workflow_template_id} disabled={isEdit} onChange={e=>setForm(f=>({...f,workflow_template_id:e.target.value}))}>
                  <option value="">No workflow / free-form job</option>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                {isEdit && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Workflow template cannot be changed after creation</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Additional notes for technician..." rows={2} />
              </div>
            </div>
            {/* Right col - Reference Documents */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {!isEdit && (
                <>
                  <div>
                    <label className="form-label" style={{ marginBottom:8, display:'block' }}>Reference Documents</label>
                    <div {...getRootProps()} className={`drop-zone ${isDragActive?'active':''}`}>
                      <input {...getInputProps()} />
                      <div className="drop-zone-icon"><FileText size={28} /></div>
                      <div className="drop-zone-title">Drop reference materials here</div>
                      <div className="drop-zone-sub">Images, Videos, PDF documents · No limit</div>
                    </div>
                  </div>
                  {files.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5 }}>
                        {files.length} file{files.length>1?'s':''} selected
                      </div>
                      {files.map((f, i) => (
                        <div key={i} style={{
                          display:'flex', alignItems:'center', gap:10,
                          background:'var(--bg-muted)', borderRadius:'var(--r-md)',
                          padding:'10px 12px', border:'1px solid var(--border)'
                        }}>
                          <div style={{ color:'var(--accent)' }}>{fileIcon(f)}</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }} className="truncate">{f.name}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{(f.size/1024).toFixed(1)} KB</div>
                          </div>
                          <button className="btn-icon" style={{ width:24, height:24, padding:0 }}
                            onClick={() => setFiles(files.filter((_,j)=>j!==i))}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              {isEdit && job.reference_documents?.length > 0 && (
                <div style={{ background:'var(--bg-muted)', borderRadius:'var(--r-lg)', padding:16, border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:10 }}>Reference Documents</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {job.reference_documents.map(doc => (
                      <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
                        <FileText size={14} color="var(--text-muted)" />
                        <span className="truncate" style={{ flex:1 }}>{doc.original_name}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:12, fontStyle:'italic' }}>
                    Go to job details to manage attachments
                  </div>
                </div>
              )}

              {/* Workflow preview */}
              {form.workflow_template_id && (() => {
                const wf = workflows.find(w => w.id === form.workflow_template_id);
                if (!wf) return null;
                return (
                  <div style={{ background:'var(--bg-muted)', borderRadius:'var(--r-lg)', padding:16, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', marginBottom:10 }}>
                      Workflow: {wf.name}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                      {(wf.steps || []).slice(0,5).map((step, i) => (
                        <div key={step.id} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:20, height:20, borderRadius:'50%', background:'var(--accent-muted)', color:'var(--accent)', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{i+1}</div>
                          <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{step.label}</span>
                          <span style={{ marginLeft:'auto', fontSize:10, color:'var(--text-muted)', background:'var(--bg-card)', padding:'1px 6px', borderRadius:4 }}>{step.type}</span>
                        </div>
                      ))}
                      {wf.steps?.length > 5 && <div style={{ fontSize:12, color:'var(--text-muted)' }}>+{wf.steps.length-5} more steps...</div>}
                    </div>
                  </div>
                );
              })()}

              {/* Linked SOPs Section */}
              {linkedSops.length > 0 && (
                <div style={{ background:'var(--accent-muted)', borderRadius:'var(--r-lg)', padding:16, border:'1px solid rgba(14,165,233,0.2)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                    <BookOpen size={16} color="var(--accent)" />
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--accent)', textTransform:'uppercase', letterSpacing:0.5 }}>
                      Linked Procedures (SOPs)
                    </span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {linkedSops.map(sop => (
                      <div key={sop.id} style={{ display:'flex', alignItems:'center', gap:10, background:'var(--bg-card)', padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)' }}>
                        <div style={{ width:6, height:6, borderRadius:'50%', background:'var(--accent)' }} />
                        <span style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{sop.title}</span>
                      </div>
                    ))}
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, fontStyle:'italic', lineHeight:1.4 }}>
                      These procedures will be attached to the job and visible to the technician during field work.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <div className="spinner" style={{borderColor:'rgba(255,255,255,0.3)',borderTopColor:'#fff'}} /> : (isEdit ? <CheckCircle size={15} /> : <Plus size={15} />)}
            {isEdit ? 'Save Changes' : 'Create Job'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [view, setView] = useState('list');
  const [showCreate, setShowCreate] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [forecasts, setForecasts] = useState([]);
  const canCreate = ['super_admin', 'admin', 'supervisor'].includes(user?.role);
  const canEdit = ['super_admin', 'admin', 'supervisor'].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    jobsAPI.list({ search, status:statusFilter, priority:priorityFilter, limit:50 })
      .then(r => { setJobs(r.data.jobs); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [search, statusFilter, priorityFilter]);

  useEffect(() => { 
    load();
    // Also load forecasts for calendar view
    schedulesAPI.forecast().then(r => setForecasts(r.data.forecasts || [])).catch(() => {});
  }, [load]);

  const handleClone = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Clone this job?')) return;
    try {
      await jobsAPI.clone(id);
      toast.success('Job cloned successfully!');
      load();
    } catch (err) {
      toast.error('Failed to clone job');
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete job');
    }
  };

  const handleEdit = (job, e) => {
    e.stopPropagation();
    setEditingJob(job);
  };

  const statusCounts = jobs.reduce((acc, j) => ({ ...acc, [j.status]: (acc[j.status]||0)+1 }), {});

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
        <div className="search-bar" style={{ flex:1, minWidth:220 }}>
          <Search size={15} color="var(--text-muted)" />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search jobs by title or number..." />
        </div>
        <div style={{ display:'flex', background:'var(--bg)', borderRadius:'var(--r-md)', padding:4, border:'1px solid var(--border)' }}>
          <button className={`btn-icon ${view==='list'?'active':''}`} style={{ background: view==='list'?'var(--bg-card)':'transparent', boxShadow: view==='list'?'0 1px 3px rgba(0,0,0,0.1)':'none' }} onClick={() => setView('list')}>
            <List size={16} /> <span style={{ fontSize:12, fontWeight:600, marginLeft:4 }}>List</span>
          </button>
          <button className={`btn-icon ${view==='calendar'?'active':''}`} style={{ background: view==='calendar'?'var(--bg-card)':'transparent', boxShadow: view==='calendar'?'0 1px 3px rgba(0,0,0,0.1)':'none' }} onClick={() => setView('calendar')}>
            <CalendarIcon size={16} /> <span style={{ fontSize:12, fontWeight:600, marginLeft:4 }}>Calendar</span>
          </button>
        </div>
        <button className="btn-icon" onClick={load}><RefreshCw size={15} /></button>
        {canCreate && (
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-ghost" onClick={() => setShowSchedules(true)}>
              <Clock size={15} /> Set PMS
            </button>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <Plus size={15} /> New Job
            </button>
          </div>
        )}
      </div>

      {/* Status filters */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        {['','pending','assigned','in_progress','completed','overdue'].map(s => (
          <button key={s} className={`filter-pill ${statusFilter===s?'active':''}`}
            onClick={() => setStatusFilter(s)}>
            {s ? s.replace('_',' ') : 'All'}
            {s && statusCounts[s] ? <span style={{ marginLeft:4, opacity:0.7 }}>({statusCounts[s]})</span> : null}
          </button>
        ))}
        <div style={{ width:1, height:20, background:'var(--border)', margin:'0 4px' }} />
        {['','critical','high','medium','low'].map(p => (
          <button key={p} className={`filter-pill ${priorityFilter===p?'active':''}`}
            onClick={() => setPriorityFilter(p)}
            style={{ borderColor: p ? PRIORITY_COLORS[p]+'44' : undefined }}>
            {p ? p : 'Any priority'}
          </button>
        ))}
      </div>

      {/* Summary strip */}
      <div style={{ display:'flex', gap:8 }}>
        {[
          { l:'Total', v:total, c:'var(--accent)' },
          { l:'Active', v:(statusCounts.pending||0)+(statusCounts.assigned||0)+(statusCounts.in_progress||0), c:'#8B5CF6' },
          { l:'Completed', v:statusCounts.completed||0, c:'#10B981' },
          { l:'Overdue', v:statusCounts.overdue||0, c:'#EF4444' },
        ].map(s => (
          <div key={s.l} style={{
            background:'var(--bg-card)', border:'1px solid var(--border)',
            borderRadius:'var(--r-md)', padding:'9px 16px',
            display:'flex', alignItems:'center', gap:8
          }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:s.c }} />
            <span style={{ fontSize:16, fontWeight:800, color:'var(--text)' }}>{s.v}</span>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>{s.l}</span>
          </div>
        ))}
      </div>

      {/* Jobs View Content */}
      {view === 'calendar' ? (
        <JobsCalendar jobs={[...jobs, ...forecasts]} onSelectJob={(job) => {
          if (job.status === 'forecasted') {
            toast('This is a planned maintenance window. It will be automatically generated as a live job when due.', { icon: 'ℹ️', duration: 4000 });
            return;
          }
          setEditingJob(job);
        }} />
      ) : (
      <div className="card">
        {loading ? (
          <div style={{ padding:32, display:'flex', justifyContent:'center' }}><div className="spinner" style={{ width:28, height:28 }} /></div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={40} className="empty-icon" />
            <div className="empty-title">No jobs found</div>
            <div className="empty-sub">{search ? 'Try different filters' : 'Create your first job to get started'}</div>
            {canCreate && <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={14} /> Create Job</button>}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Asset</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/jobs/${job.id}`)}>
                    <td>
                      <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                        <span style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--accent)', fontWeight:600 }}>{job.job_number}</span>
                        <span style={{ fontSize:14, fontWeight:700, color:'var(--text)', maxWidth:240 }} className="truncate">{job.title}</span>
                        {job.workflow_name && <span style={{ fontSize:11, color:'var(--text-muted)' }}>{job.workflow_name}</span>}
                      </div>
                    </td>
                    <td>
                      {job.asset_name ? (
                        <div>
                          <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{job.asset_name}</div>
                          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{job.asset_location}</div>
                        </div>
                      ) : <span style={{ color:'var(--text-muted)', fontSize:13 }}>—</span>}
                    </td>
                    <td>
                      {job.assigned_to_name ? (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div className="avatar avatar-sm" style={{ background:'#0EA5E9' }}>
                            {job.assigned_to_name.split(' ').map(p=>p[0]).join('').slice(0,2)}
                          </div>
                          <span style={{ fontSize:13 }}>{job.assigned_to_name}</span>
                        </div>
                      ) : <span style={{ color:'var(--text-muted)', fontSize:13 }}>Unassigned</span>}
                    </td>
                    <td><Badge s={job.status} /></td>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                        <div style={{ width:7, height:7, borderRadius:'50%', background:PRIORITY_COLORS[job.priority] }} />
                        <span style={{ fontSize:13, fontWeight:600, color:PRIORITY_COLORS[job.priority] }}>{job.priority}</span>
                      </div>
                    </td>
                    <td>
                      {job.due_date ? (
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <CalendarIcon size={12} color="var(--text-muted)" />
                          <span style={{
                            fontSize:12, fontWeight:600,
                            color: new Date(job.due_date) < new Date() && !['completed','cancelled'].includes(job.status) ? '#EF4444' : 'var(--text-secondary)'
                          }}>
                            {format(parseISO(job.due_date), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      ) : <span style={{ color:'var(--text-muted)', fontSize:13 }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
                        {job.status === 'completed' && (
                          <button className="btn-icon" title="View Report" style={{ color:'var(--success)' }} onClick={(e) => { e.stopPropagation(); setViewingReport(job.id); }}><Eye size={14} /></button>
                        )}
                        <button className="btn-icon" title="Edit" onClick={(e) => handleEdit(job, e)}><Edit2 size={14} /></button>
                        <button className="btn-icon" title="Clone" onClick={(e) => handleClone(job.id, e)}><Copy size={14} /></button>
                        {['super_admin', 'admin'].includes(user?.role) && (
                          <button className="btn-icon" title="Delete" style={{ color:'var(--danger)' }} onClick={(e) => handleDelete(job.id, e)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {(showCreate || editingJob) && (
        <JobFormModal 
          job={editingJob} 
          onClose={() => { setShowCreate(false); setEditingJob(null); }} 
          onSave={() => { setShowCreate(false); setEditingJob(null); load(); }} 
        />
      )}

      {showSchedules && <PMSchedulesModal onClose={() => setShowSchedules(false)} />}
      {viewingReport && <ReportViewerModal jobId={viewingReport} onClose={() => setViewingReport(null)} />}
    </div>
  );
}
